import type { VoidFunctionComponent } from 'react';

import Text from '../../../generic/Text';

import styles from './ScimProvisioning.module.less';

type SupportedAttribute = {
  attribute: string;
  description: string;
};

/**
 * Mirrors what `modules/ScimManager/userMapping` actually reads, and what `scimResources` advertises in
 * `/Schemas` — an identity provider mapping anything else is told so rather than having the value dropped.
 */
const SUPPORTED_ATTRIBUTES: SupportedAttribute[] = [
  { attribute: 'userName', description: 'The user’s FOSSA username. Required.' },
  {
    attribute: 'emails[type eq "work"].value',
    description: 'The user’s email address. Only work addresses are accepted; home and other are rejected.',
  },
  {
    attribute: 'name.formatted, displayName, name.givenName, name.familyName',
    description: 'The user’s full name. The formatted name wins, then displayName, then given and family combined.',
  },
  { attribute: 'active', description: 'Whether the user is enabled. Deactivating disables the user; it never deletes them.' },
  { attribute: 'externalId', description: 'Your identity provider’s own identifier, used to match users. Not stored.' },
];

/** Documents the SCIM attributes this endpoint maps, so an admin can build their mapping without guessing. */
const SupportedAttributes: VoidFunctionComponent = () => (
  <div className={styles.attributes}>
    <Text.H5>Supported attributes</Text.H5>
    <Text.Paragraph>
      FOSSA implements the SCIM 2.0 <code>/Users</code> resource. Only the attributes below are stored — anything
      else your identity provider sends is rejected rather than silently discarded, so a mapping that succeeds is
      a mapping that took effect. Groups are not supported. Organization roles are not provisioned over SCIM:
      a new user takes your organization’s default role, and roles are managed in FOSSA or through SSO.
    </Text.Paragraph>
    <table className={styles.attributesTable}>
      <thead>
        <tr>
          <th>Attribute</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {SUPPORTED_ATTRIBUTES.map(({ attribute, description }) => (
          <tr key={attribute}>
            <td><code className={styles.attributeName}>{attribute}</code></td>
            <td>{description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default SupportedAttributes;
